// Acts on anchors in the DOM, click events (capture phase so we beat handlers
// that build URLs at click time), and window.open. Re-checks on mutations.

function compilePatterns(patterns) {
	return patterns
		.filter((p) => p.pattern)
		.map((p) => {
			let test;
			if (p.match_type === "regex") {
				try {
					const re = new RegExp(p.pattern, "i");
					test = (url) => re.test(url);
				} catch (e) {
					test = () => false;
				}
			} else if (p.match_type === "host") {
				const needle = p.pattern.toLowerCase();
				test = (url) => {
					try {
						return new URL(url, window.location.href).host.toLowerCase().includes(needle);
					} catch (e) {
						return url.toLowerCase().includes(needle);
					}
				};
			} else {
				const needle = p.pattern.toLowerCase();
				test = (url) => (url || "").toLowerCase().includes(needle);
			}
			return { ...p, test };
		});
}

function matchUrl(url, rules) {
	if (!url) return null;
	for (const rule of rules) if (rule.test(url)) return rule;
	return null;
}

function stripLink(a) {
	// Replace the anchor with a span so the rebranded label stays visible.
	const span = document.createElement("span");
	span.className = "wl-stripped";
	while (a.firstChild) span.appendChild(a.firstChild);
	if (a.parentNode) a.parentNode.replaceChild(span, a);
}

function scrubAnchors(root, rules) {
	const anchors = root.nodeType === 1 ? [...root.querySelectorAll("a[href]"), ...(root.matches && root.matches("a[href]") ? [root] : [])] : [];
	anchors.forEach((a) => {
		const href = a.getAttribute("href");
		const rule = matchUrl(href, rules);
		if (!rule) return;
		if (rule.action === "remove") {
			stripLink(a);
		} else if (rule.action === "redirect" && rule.redirect_to) {
			a.setAttribute("href", rule.redirect_to);
		} else if (rule.action === "block") {
			a.setAttribute("href", "javascript:void(0)");
			a.setAttribute("aria-disabled", "true");
			a.dataset.wlBlocked = "1";
		}
	});
}

export function installLinkBlocker(policy) {
	if (!policy || !policy.enabled) return () => {};
	const rules = compilePatterns(policy.patterns || []);
	if (!rules.length) return () => {};

	scrubAnchors(document.body, rules);

	// Capture phase so we run before any framework router does.
	const onClick = (e) => {
		const a = e.target.closest && e.target.closest("a[href]");
		if (!a) return;
		const rule = matchUrl(a.getAttribute("href"), rules);
		if (!rule) return;
		if (rule.action === "block" || rule.action === "remove") {
			e.preventDefault();
			e.stopPropagation();
		} else if (rule.action === "redirect" && rule.redirect_to) {
			e.preventDefault();
			e.stopPropagation();
			window.location.href = rule.redirect_to;
		}
	};
	document.addEventListener("click", onClick, true);

	// window.open guard
	const origOpen = window.open;
	window.open = function (url, ...rest) {
		const rule = matchUrl(url, rules);
		if (rule && (rule.action === "block" || rule.action === "remove")) return null;
		if (rule && rule.action === "redirect" && rule.redirect_to) return origOpen.call(window, rule.redirect_to, ...rest);
		return origOpen.call(window, url, ...rest);
	};

	const observer = new MutationObserver((muts) => {
		for (const m of muts) {
			m.addedNodes.forEach((n) => {
				if (n.nodeType === 1) scrubAnchors(n, rules);
			});
			if (m.type === "attributes" && m.attributeName === "href" && m.target.nodeType === 1) {
				scrubAnchors(m.target, rules);
			}
		}
	});
	observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["href"] });

	return () => {
		document.removeEventListener("click", onClick, true);
		observer.disconnect();
		window.open = origOpen;
	};
}
